import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIsReviewEnabled } from './mlkActions/useIsReviewEnabled';

interface PosteReviewState {
  visible: boolean;
  modalAvailable: boolean;
  checkModal: () => boolean;
  bound: boolean;
  setUp: (config: any) => void;
}

const DEFAULT_STATE: PosteReviewState = {
  visible: false,
  modalAvailable: false,
  checkModal: () => false,
  bound: false,
  setUp: () => {},
};

export const PosteReviewContext = React.createContext<
  [
    PosteReviewState,
    React.Dispatch<React.SetStateAction<PosteReviewState>> | (() => void)
  ]
>([DEFAULT_STATE, () => {}]);

export function useSetupPosteReview(iframe: HTMLIFrameElement | null) {
  const [state, setState_] = useState<PosteReviewState>(DEFAULT_STATE);
  function setState(newState: any) {
    return setState_(newState);
  }

  useEffect(() => {
    function check() {
      if (!state.bound) {
        let done = false;
        if (iframe?.contentWindow) {
          const w = iframe.contentWindow as any;
          if (w.setUpPosteModal) {
            setState({
              ...state,
              bound: true,
              setUp: w.setUpPosteModal,
              checkModal: w.checkModal,
            });
            // w.console = console;
            done = true;
          }
        }
        if (!done) {
          setTimeout(check, 500);
        }
      }
    }
    check();
  }, [state, state.bound, iframe]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (state.bound) {
        const available = state.checkModal();
        if (
          (state.visible && !available) ||
          available !== state.modalAvailable
        ) {
          setState({
            ...state,
            visible: state.visible && available,
            modalAvailable: available,
          });
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [state]);

  return [
    function Context({ children }: React.PropsWithChildren<{}>) {
      return (
        <PosteReviewContext.Provider value={[state, setState]}>
          {children}
        </PosteReviewContext.Provider>
      );
    },
    state as any,
  ];
}

export default function usePosteReview() {
  const [state, setState] = useContext(PosteReviewContext);
  const [config, setConfig] = useState({});
  const [lastConfig, setLastConfig] = useState({});
  const { posteMode } = useIsReviewEnabled();

  function showModal() {
    if (!posteMode) return;
    const modalPresent = state.checkModal();
    if (modalPresent) {
      setState({
        ...state,
        visible: true,
      });
    }
  }

  const init = useMemo(() => {
    let interval: any;
    let timeoutOfTheTimeout: any;
    function openWhenAvailable(timeout = 5000) {
      if (interval) {
        clearInterval(interval);
        clearTimeout(timeoutOfTheTimeout);
      }
      interval = setInterval(() => {
        const modalPresent = state.checkModal();
        if (modalPresent) {
          clearInterval(interval);
          setState({
            ...state,
            visible: true,
          });
        }
      }, 500);
      timeoutOfTheTimeout = setTimeout(
        () => interval && clearInterval(interval),
        timeout
      );
    }

    return function (config_: any) {
      if (!posteMode) return;
      const {
        orderCode,
        scriptMode,
        surveyId,
        metadata,
        lastAttempt,
        autoOpen,
      } = config_;
      setLastConfig(config_);
      if (state.bound) {
        state.setUp({
          orderCode: `${orderCode}`,
          scriptMode,
          surveyId,
          metadata,
          lastAttempt,
        });
      } else {
        setConfig({
          orderCode: `${orderCode}`,
          scriptMode,
          surveyId,
          metadata,
          lastAttempt,
        });
      }
      if (autoOpen) {
        openWhenAvailable();
      }
    };
  }, [state, setConfig, setState, posteMode]);

  useEffect(() => {
    if (state.bound && Object.keys(config).length > 0) {
      state.setUp(config);
      setConfig({});
    }
  }, [config, state]);

  return { state, showModal, init, lastConfig };
}
